import { Button, Divider, Icon, Stack, Text, Tooltip } from '@chakra-ui/react';
import { MailIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';
import { useTranslations } from 'use-intl';

import {
  FundraisingInvestStatus,
  FundraisingStatus,
  useGetSubscriptionQuery,
  useRemindSubscriptionInvitationMutation,
} from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import { useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';

type Props = {
  subscriptionId: string;
};

/**
 * SubscriptionDetailsReminder.
 * Remind button logic section of the subscription side panel.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsReminder({ subscriptionId }: Props): JSX.Element | null {
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { data, refetch } = useGetSubscriptionQuery({ variables: { subscriptionId } });

  const [remindSubscriber, { loading: remindLoading }] = useRemindSubscriptionInvitationMutation();

  const subscription = data?.subscription;

  /* ** Display remind block ** */
  const shouldDisplayRemind =
    subscription?.investStatus !== FundraisingInvestStatus.APPROVED &&
    subscription?.fundraising.status === FundraisingStatus.STARTED;

  if (!shouldDisplayRemind) return null;

  /* ** Enable remind button less than 24 hours ** */
  const shouldRemind = dayjs(subscription?.lastEmailSentAt).isBefore(dayjs().subtract(1, 'h'));

  /* ** On remind button click ** */
  const handleSendReminder = (): void => {
    remindSubscriber({
      variables: {
        subscriptionId,
      },
      onCompleted: () => {
        successToast({ title: i18nSubscriberDetailsSidePanel('Toast.RemindSuccess') });
        refetch();
      },
      onError: () => {
        errorToast({ title: i18nSubscriberDetailsSidePanel('Toast.RemindSuccess') });
      },
    });
  };

  return (
    <>
      <Divider />
      <Stack px="4" spacing="4">
        <Text color="gray.600" fontWeight="600">
          {i18nSubscriberDetailsSidePanel('RemindInvite')}
        </Text>
        <Stack alignItems="flex-start" spacing="0">
          <Tooltip
            hasArrow
            isDisabled={shouldRemind}
            label={i18nSubscriberDetailsSidePanel('Tooltip')}
            placement="top"
          >
            <Button
              isDisabled={!shouldRemind}
              isLoading={remindLoading}
              leftIcon={<Icon as={MailIcon} />}
              loadingText={i18nSubscriberDetailsSidePanel('RemindInviteButtonLoading')}
              size="sm"
              onClick={handleSendReminder}
            >
              {i18nSubscriberDetailsSidePanel('RemindInviteButton')}
            </Button>
          </Tooltip>
          <Text fontSize="xs">
            {i18nSubscriberDetailsSidePanel('LastEmailSent')}
            {subscription?.lastEmailSentAt && (
              <Text as="span" fontWeight="500">
                {formatDate(subscription?.lastEmailSentAt, 'D MMM YYYY')}
              </Text>
            )}
          </Text>
        </Stack>
      </Stack>
    </>
  );
}

export type SubscriptionDetailsReminderProps = Props;
